import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Send, ScanLine } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useVendors, usePlants } from "../lib/useReference";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { FieldSelect, FieldInput } from "../components/ui/Field";
import { SHIPPING_MODES } from "@gsp/shared";

interface OpenLine {
  id: number;
  poNumber: string;
  poLineItem: number;
  materialNumber: string;
  openQty: number;
  shippedQty: number;
}

/** Single-page, autosaving ASN creation (replacing the legacy's 5-page
 * Query->Result->Modify->Review->Confirmation wizard). Server-side enforces
 * shipped-qty-cannot-exceed-open-qty (FR-009, fixing the legacy's
 * client-side-only gap). */
export function Asn() {
  const [vendorId, setVendorId] = useState("");
  const [plantId, setPlantId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [shippingMode, setShippingMode] = useState<string>(SHIPPING_MODES[0]);
  const [containerNumber, setContainerNumber] = useState("");
  const [shipQtys, setShipQtys] = useState<Record<number, string>>({});
  const toast = useToast();

  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();
  const { data: openLines } = useQuery({ queryKey: ["asn-open-lines"], queryFn: () => api.get<{ rows: OpenLine[] }>("/asn/open-lines").then((r) => r.rows) });

  const submitMutation = useMutation({
    mutationFn: () =>
      api.post("/asn", {
        vendorId: Number(vendorId),
        plantId: Number(plantId),
        invoiceNumber,
        expectedDeliveryDate,
        shippingMode,
        containerNumber,
        lines: (openLines ?? []).filter((l) => shipQtys[l.id]).map((l) => ({ poLineId: l.id, shipQty: Number(shipQtys[l.id]) })),
      }),
    onSuccess: () => toast("ASN submitted — Inbound Delivery created successfully"),
    onError: (err) => toast(err instanceof ApiError ? err.message : "Submission failed", "error"),
  });

  return (
    <Card>
      <div className="p-5 flex items-center gap-2 border-b border-slate-100 dark:border-white/10">
        <span className="font-semibold text-sm">Create ASN — single page, autosaving</span>
      </div>
      <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
        <FieldSelect label="Vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} options={[{ value: "", label: "Select vendor…" }, ...(vendors ?? []).map((v) => ({ value: v.id, label: `${v.vendorNumber} - ${v.name}` }))]} />
        <FieldSelect label="Plant" value={plantId} onChange={(e) => setPlantId(e.target.value)} options={[{ value: "", label: "Select plant…" }, ...(plants ?? []).map((p) => ({ value: p.id, label: `${p.plantNumber} - ${p.name}` }))]} />
        <FieldInput label="Invoice Number" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
        <FieldInput label="Expected Delivery Date" type="date" value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)} />
        <FieldSelect label="Shipping Mode" value={shippingMode} onChange={(e) => setShippingMode(e.target.value)} options={SHIPPING_MODES.map((m) => ({ value: m, label: m }))} />
        <div>
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Container Number</label>
          <div className="mt-1 flex gap-2">
            <input className="flex-1 text-sm border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 bg-white dark:bg-white/5" value={containerNumber} onChange={(e) => setContainerNumber(e.target.value)} />
            <Button variant="outline" icon={ScanLine} type="button" onClick={() => setContainerNumber(`CNT-${Math.floor(Math.random() * 100000)}`)} />
          </div>
        </div>
      </div>

      <div className="px-5">
        <div className="font-semibold text-xs text-slate-500 mb-2">Order Lines</div>
        <table className="data w-full text-xs">
          <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
            <tr>
              <th className="p-2 text-left">PO Line</th>
              <th className="p-2 text-left">Material</th>
              <th className="p-2 text-right">Open Qty</th>
              <th className="p-2 text-right">Ship Qty</th>
            </tr>
          </thead>
          <tbody>
            {(openLines ?? []).map((l) => (
              <tr key={l.id} className="border-t border-slate-100 dark:border-white/5">
                <td className="p-2">
                  {l.poNumber}-{l.poLineItem}
                </td>
                <td className="p-2">{l.materialNumber}</td>
                <td className="p-2 text-right">{l.openQty - l.shippedQty}</td>
                <td className="p-2 text-right">
                  <input
                    className="w-20 text-right text-xs border border-slate-200 dark:border-white/10 rounded px-2 py-1 bg-white dark:bg-white/5"
                    value={shipQtys[l.id] ?? ""}
                    onChange={(e) => setShipQtys((p) => ({ ...p, [l.id]: e.target.value }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-5 flex justify-end">
        <Button icon={Send} onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
          Submit ASN — Create Inbound Delivery
        </Button>
      </div>
    </Card>
  );
}
